import { SelectInput, styled } from 'canoe';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';

import { WebFontResource } from '@candu-ai/styleguide/custom-fonts';

import { useGoogleFonts } from './hooks/useGoogleFonts';

const Container = styled.div`
  width: 180px;

  > div {
    width: 100%;
  }
`;

interface Props {
  value?: string;
  onChange: (font: WebFontResource) => void;
  label?: string;
  disabled?: boolean;
  customFonts?: WebFontResource[];
}

const SystemFont: WebFontResource = {
  id: 'system-ui',
  family: 'system-ui',
  type: 'system',
};

const sortFonts = (a: WebFontResource, b: WebFontResource) => (a.family > b.family ? 1 : -1);

export const FontPicker = ({ value = '', onChange, disabled, customFonts }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const googleFonts = useGoogleFonts();

  const fonts = [
    ...(customFonts || []).sort(sortFonts),
    ...googleFonts.sort(sortFonts),
    SystemFont,
  ];
  const items = fonts.map(({ family }) => ({
    label: family,
    value: family,
  }));

  const onClearSearch = useCallback(() => {
    setSearchValue('');
  }, []);
  const filteredItems = useMemo(() => {
    if (!searchValue) {
      return items;
    }

    return items.filter((item) => {
      return item.label.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, items]);

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  return (
    <Container>
      <SelectInput
        searchable
        clearable={false}
        onFilter={updateSearchValue}
        onOpeningChange={onClearSearch}
        onClearFilter={onClearSearch}
        searchInputProps={{
          placeholder: `Search a font`,
        }}
        disabled={disabled}
        selectedItem={items.find((item) => item.value === value)}
        items={filteredItems}
        onSelect={(selected) => {
          if (selected) {
            onChange(fonts.find((font) => font.family === selected.value) as WebFontResource);
          }
        }}
      />
    </Container>
  );
};
